import { DEFAULT_MASJID_ID } from "../config/apiConfig";

/* ================================
   API & CDN CONFIG
================================ */
const API_BASE =
  process.env.REACT_APP_CONNECTUMMAH_API_BASE?.replace(/\/+$/, "") ||
  "https://api.connectummah.com/api";

const CDN_BASE =
  process.env.REACT_APP_CONNECTUMMAH_CDN_BASE?.replace(/\/+$/, "") ||
  "https://cdn.connectummah.com";

/* ================================
   ENDPOINT BUILDER
================================ */
const endpointFor = (masjidId) => {
  const parsedMasjidId = Number(masjidId);
  const validMasjidId =
    Number.isInteger(parsedMasjidId) && parsedMasjidId > 0
      ? parsedMasjidId
      : DEFAULT_MASJID_ID;

  return `${API_BASE}/Masjid_Services/GetMasjid_ServicesForMasjid/${validMasjidId}`;
};

/* ================================
   IMAGE URL RESOLVER (IMPORTANT)
================================ */
function resolveImageUrl(image) {
  if (!image) return "";

  const img = String(image).trim();

  // Already absolute (http/https)
  if (/^https?:\/\//i.test(img)) {
    return img;
  }

  // Remove leading slashes
  const clean = img.replace(/^\/+/, "");

  // If API already contains folder
  if (clean.startsWith("myuploadfiles/")) {
    return `${CDN_BASE}/${clean}`;
  }

  // Otherwise prepend folder
  return `${CDN_BASE}/myuploadfiles/${clean}`;
}

/* ================================
   NORMALIZER
================================ */
function normalizeServices(raw) {
  const list = Array.isArray(raw) ? raw : [];

  return list.map((s, i) => ({
    id:
      s?.id ??
      s?.serviceId ??
      s?.ServiceId ??
      s?.ServiceID ??
      s?.Service_ID ??
      i + 1,

    title:
      s?.title ??
      s?.name ??
      s?.service_Name ??
      s?.ServiceName ??
      s?.serviceName ??
      "Service",

    description:
      s?.description ??
      s?.desc ??
      s?.service_Description ??
      s?.ServiceDescription ??
      s?.serviceDescription ??
      "",

    image: resolveImageUrl(
      s?.ImageLink ??
      s?.imageLink ??
      s?.image ??
      s?.Image ??
      ""
    ),

    schedule:
      s?.schedule ??
      s?.Schedule ??
      s?.time ??
      s?.Time ??
      s?.timing ??
      s?.Timing ??
      "",
  }));
}

/* ================================
   SAFE JSON PARSER
================================ */
async function safeJson(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/* ================================
   FETCH WITH RETRY
================================ */
async function fetchWithRetry(url, options = {}, tries = 2) {
  let lastError;

  for (let i = 0; i < tries; i++) {
    try {
      const res = await fetch(url, options);

      if (!res.ok) {
        if (res.status >= 500 && i < tries - 1) {
          await new Promise((r) => setTimeout(r, 300));
          continue;
        }

        const body = await safeJson(res);
        throw new Error(
          `HTTP ${res.status} ${body?.message || body?.error || ""}`
        );
      }

      return res;
    } catch (err) {
      lastError = err;
      if (i < tries - 1) {
        await new Promise((r) => setTimeout(r, 250));
      }
    }
  }

  throw lastError;
}

/* ================================
   MAIN EXPORT FUNCTION
================================ */
export async function getMasjidServices(masjidId, { signal } = {}) {
  const url = endpointFor(masjidId);

  const localController = !signal ? new AbortController() : null;
  const usedSignal = signal || localController.signal;

  let timer;
  if (localController) {
    timer = setTimeout(() => localController.abort(), 12000);
  }

  try {
    const res = await fetchWithRetry(url, {
      signal: usedSignal,
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    const data = await safeJson(res);
    if (!data) return [];

    return normalizeServices(data);
  } catch (err) {
    console.warn("getMasjidServices error:", err?.message || err);
    return [];
  } finally {
    if (timer) clearTimeout(timer);
  }
}
